/**
 * The upgrade link: /settings/billing?plan=PRODUCT_ID[&code=CODE].
 *
 * The admin console hands it out when an upgrade needs a card the customer
 * hasn't added. Signed in, linking opens Billing and BillingScreen opens the
 * subscribe step from the query. Signed out, boot replaces the URL with /login,
 * so RootNavigator stashes what this returns and reopens Billing with it after
 * sign-in (the same stash-and-drain as the project links, ED-2633).
 *
 * Web only: BillingScreen's subscribe step is the web checkout. Origin-gated
 * like extractProjectChatDeepLink, and the values are bounded so the link can't
 * carry arbitrary text into the URL BillingScreen reads.
 */
import { isOwnAppUrl } from './appUrl';

export interface BillingUpgradeLink {
  plan: string;
  code?: string;
}

const PLAN_RE = /^[A-Za-z0-9_-]{1,100}$/;
const CODE_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function extractBillingUpgradeLink(
  url: string | null | undefined,
  currentWebOrigin: string | null | undefined = typeof window !== 'undefined'
    ? window.location?.origin
    : undefined,
): BillingUpgradeLink | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  let sameOrigin = false;
  try {
    sameOrigin = !!currentWebOrigin && parsed.origin === new URL(currentWebOrigin).origin;
  } catch {
    sameOrigin = false;
  }
  if (!sameOrigin && !isOwnAppUrl(url)) return null;
  if (!/^\/settings\/billing\/?$/i.test(parsed.pathname)) return null;

  const plan = parsed.searchParams.get('plan') || '';
  if (!PLAN_RE.test(plan)) return null;
  const code = parsed.searchParams.get('code') || '';
  return CODE_RE.test(code) ? { plan, code } : { plan };
}
